import React, { useState } from 'react'
import { IonIcon } from '@ionic/react'
import { Form, FormikProvider, useFormik } from 'formik'
import styled from '@emotion/styled'
import { Link, useHistory } from 'react-router-dom'
import { bindActionCreators } from 'redux'
import { useDispatch, useSelector } from 'react-redux'
import {
  arrowBackOutline,
  arrowForwardOutline,
  arrowUpOutline,
  mailOutline,
  logoWhatsapp,
} from 'ionicons/icons'
import { authActions } from './AuthStore'
import { IStateRedux } from '../common/ReduxStore'
import { Yup } from '../common/Yup'
import { useOpenJivoChat } from '../common/hooks/useJivoApi'
import { AuthPageLayout } from './AuthPageLayout'
import {
  AUTH_COLORS,
  AUTH_MOBILE_BREAKPOINT,
  AuthField,
  AuthFormBox,
  AuthPrimaryButton,
  AuthSubtitle,
  AuthTitle,
  maskCpf,
} from './AuthForm'

type DadosRecuperacaoSenha = {
  primeiroNome: string
  emailMascarado: string
}

const WHATSAPP_URL =
  'https://wa.me/556291851367?text=Ol%C3%A1!%20Estou%20tentando%20recuperar%20minha%20senha%20na%20New%20Driver%20e%20preciso%20de%20ajuda.%20Pode%20me%20ajudar%3F'

export const EsqueceuSenha = () => {
  const {
    Notice,
    LoginLink,
    SupportBox,
    SuccessCard,
    SuccessIcon,
    SuccessTitle,
    SuccessText,
    BackButton,
  } = EsqueceuSenha
  const dispatch = useDispatch()
  const history = useHistory()
  const actions = bindActionCreators(authActions, dispatch)
  const loading = useSelector((s: IStateRedux) => s.auth.request.fetching)
  const { openJivoChat } = useOpenJivoChat()
  const [dadosRecuperacao, setDadosRecuperacao] = useState<DadosRecuperacaoSenha | null>(null)
  const [erroRecuperacao, setErroRecuperacao] = useState('')

  const formik = useFormik({
    initialValues: { usuario: '' },
    validateOnMount: true,
    validationSchema: Yup.object().shape({
      usuario: (Yup as any).string().required().cpf(),
    }),
    onSubmit: (values) => {
      actions.recuperaSenha({
        cpf: values.usuario,
        callback: (dados) => setDadosRecuperacao(dados),
        errorCallback: (message) => setErroRecuperacao(message),
      })
    },
  })

  const handleCpfChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setErroRecuperacao('')
    formik.setFieldValue('usuario', maskCpf(event.target.value))
  }

  const handleOpenSupport = () => {
    openJivoChat({
      customData: [
        { title: 'Local', content: 'Recuperar senha' },
        { title: 'Motivo', content: 'Ajuda para recuperar senha' },
        { title: 'CPF', content: formik.values.usuario },
      ],
    })
  }

  const handleOpenWhatsapp = () => {
    window.open(WHATSAPP_URL, '_blank', 'noopener,noreferrer')
  }

  const usuarioError =
    formik.touched.usuario && formik.errors.usuario ? formik.errors.usuario : erroRecuperacao
  const hasUsuarioError = !!usuarioError
  const isSubmitDisabled = loading || !formik.isValid || !formik.values.usuario

  return (
    <AuthPageLayout variant="recovery">
      {dadosRecuperacao ? (
        <SuccessCard>
          <SuccessIcon>
            <IonIcon className="mail" icon={mailOutline} />
          </SuccessIcon>
          <SuccessTitle>
            Tudo certo, {dadosRecuperacao.primeiroNome}! Link a caminho.
          </SuccessTitle>
          <SuccessText>
            Enviamos as instruções seguras de acesso para o e-mail:{' '}
            <strong>{dadosRecuperacao.emailMascarado}</strong>
          </SuccessText>
          <Notice className="success-notice">
            <strong>Aviso:</strong> Pode levar até <strong>3 minutos para chegar.</strong> Lembre-se
            de verificar sua pasta de Spam ou Lixo Eletrônico se não encontrar na caixa principal.
          </Notice>
          <BackButton type="button" onClick={() => history.push('/')}>
            <IonIcon icon={arrowBackOutline} />
            VOLTAR PARA O LOGIN
          </BackButton>
          <SupportBox className="success-support">
            <p>Não encontrou o e-mail ou não tem mais acesso a ele?</p>
            <button className="chat" type="button" onClick={handleOpenSupport}>
              ABRIR CHAT ONLINE
              <IonIcon icon={arrowUpOutline} />
            </button>
            <button className="whatsapp" type="button" onClick={handleOpenWhatsapp}>
              Suporte pelo Whatsapp
              <IonIcon icon={logoWhatsapp} />
            </button>
          </SupportBox>
        </SuccessCard>
      ) : (
        <AuthFormBox>
          <AuthTitle>
            <strong>Esqueceu a senha?</strong> A gente resolve rápido.
          </AuthTitle>
          <AuthSubtitle>
            <strong>Digite o seu CPF abaixo.</strong> Vamos enviar um link seguro para você
            cadastrar uma nova senha e voltar para as aulas.
          </AuthSubtitle>
          <Notice>
            <strong>Aviso:</strong> O link será enviado para o{' '}
            <strong>e-mail que você usou no momento da matrícula.</strong> Lembre-se de verificar a
            pasta de Spam ou Lixo Eletrônico.
          </Notice>
          <FormikProvider value={formik}>
            <Form noValidate onSubmit={formik.handleSubmit}>
              <AuthField hasError={hasUsuarioError}>
                <label htmlFor="usuario">CPF</label>
                <input
                  autoFocus
                  id="usuario"
                  name="usuario"
                  type="tel"
                  inputMode="numeric"
                  maxLength={14}
                  placeholder="000.000.000-00"
                  value={formik.values.usuario}
                  onBlur={formik.handleBlur}
                  onChange={handleCpfChange}
                  aria-invalid={hasUsuarioError}
                />
                {usuarioError ? <p className="field-error">{usuarioError}</p> : null}
              </AuthField>
              <AuthPrimaryButton type="submit" disabled={isSubmitDisabled}>
                {loading ? (
                  'ENVIANDO...'
                ) : (
                  <>
                    ENVIAR LINK DE RECUPERAÇÃO
                    <IonIcon icon={arrowForwardOutline} />
                  </>
                )}
              </AuthPrimaryButton>
              <LoginLink>
                <span>Lembrou a senha?</span> <Link to="/">Voltar para o Login</Link>
              </LoginLink>
            </Form>
          </FormikProvider>
          <SupportBox>
            <p>Não tem acesso ao e-mail/celular cadastrado?</p>
            <button className="chat" type="button" onClick={handleOpenSupport}>
              ABRIR CHAT ONLINE
              <IonIcon icon={arrowUpOutline} />
            </button>
            <button className="whatsapp" type="button" onClick={handleOpenWhatsapp}>
              Suporte pelo Whatsapp
              <IonIcon icon={logoWhatsapp} />
            </button>
          </SupportBox>
        </AuthFormBox>
      )}
    </AuthPageLayout>
  )
}

EsqueceuSenha.Notice = styled.p`
  width: 100%;
  margin: 20px 0 0;
  border-radius: 8px;
  background: #f3cd0024;
  color: #867625;
  font-size: 14px;
  line-height: 1;
  padding: 10px 15px;

  strong {
    font-weight: 500;
  }

  @media (max-width: ${AUTH_MOBILE_BREAKPOINT}) {
    padding: 10px 12px;
  }
`

EsqueceuSenha.LoginLink = styled.div`
  margin-top: 24px;
  color: ${AUTH_COLORS.blue};
  font-size: 16px;
  line-height: 20px;
  text-align: center;

  span {
    color: ${AUTH_COLORS.orange};
  }

  a {
    color: ${AUTH_COLORS.blue};
    text-decoration: none;
  }

  a:hover {
    text-decoration: underline;
  }
`

EsqueceuSenha.SupportBox = styled.div`
  width: 100%;
  margin-top: 60px;
  border: 1px solid ${AUTH_COLORS.blue};
  border-radius: 6px;
  padding: 24px;
  text-align: center;

  p {
    margin: 0;
    color: ${AUTH_COLORS.blue};
    font-size: 16px;
    line-height: 20px;
  }

  button {
    width: 100%;
    border: 0;
    background: transparent;
    font-family: Rubik, sans-serif;
    cursor: pointer;
  }

  .chat {
    height: 48px;
    margin-top: 16px;
    border: 1px solid ${AUTH_COLORS.orange};
    border-radius: 6px;
    color: ${AUTH_COLORS.orange};
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    transition:
      background 0.2s ease,
      color 0.2s ease;
  }

  .chat:hover {
    background: ${AUTH_COLORS.orange};
    color: #ffffff;
  }

  .whatsapp {
    margin-top: 24px;
    color: #34a853;
    font-size: 16px;
    line-height: 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
  }

  .whatsapp:hover {
    text-decoration: underline;
  }

  @media (max-width: ${AUTH_MOBILE_BREAKPOINT}) {
    margin-top: 50px;
    border: 0;
    padding: 0;

    .chat {
      margin-top: 15px;
    }
  }
`

EsqueceuSenha.SuccessCard = styled.section`
  width: 100%;
  max-width: 580px;
  border: 1px solid #34a853;
  border-radius: 8px;
  background: #ffffff;
  padding: 34px;
  text-align: center;

  .success-notice {
    margin-top: 24px;
  }

  .success-support {
    margin-top: 24px;
  }

  @media (max-width: ${AUTH_MOBILE_BREAKPOINT}) {
    max-width: none;
    border: 0;
    padding: 0;
    text-align: left;

    .success-support {
      margin-top: 50px;
      text-align: center;
    }
  }
`

EsqueceuSenha.SuccessIcon = styled.div`
  width: 75px;
  height: 75px;
  margin: 0 auto;
  color: ${AUTH_COLORS.blue};
  display: flex;
  align-items: center;
  justify-content: center;

  .mail {
    width: 75px;
    height: 75px;
  }

  @media (max-width: ${AUTH_MOBILE_BREAKPOINT}) {
    display: none;
  }
`

EsqueceuSenha.SuccessTitle = styled.h1`
  margin: 24px 0 0;
  color: ${AUTH_COLORS.blue};
  font-size: 38px;
  font-weight: 600;
  line-height: 1;

  @media (max-width: ${AUTH_MOBILE_BREAKPOINT}) {
    margin-top: 0;
    font-size: 30px;
  }
`

EsqueceuSenha.SuccessText = styled.p`
  margin: 24px 0 0;
  color: ${AUTH_COLORS.blue};
  font-size: 16px;
  line-height: 24px;

  strong {
    font-weight: 500;
  }

  @media (max-width: ${AUTH_MOBILE_BREAKPOINT}) {
    margin-top: 20px;
  }
`

EsqueceuSenha.BackButton = styled(AuthPrimaryButton)``
